import { useCallback, useMemo, useState } from "react";
import {
  Authenticated,
  useGo,
  useTranslate,
  type CrudFilter,
  type CrudSort,
} from "@refinedev/core";
import { Col, ConfigProvider, Flex, Row, Space } from "antd";
import { useDebouncedCallback } from "use-debounce";
import { useSession } from "hooks/useSession";
import { useWorkspace } from "hooks/useWorkspace";
import { useLiveViewerCount } from "hooks/useLiveViewerCount";
import { StatCard } from "../components/analytics/StatCard";
import { formatPct } from "../components/analytics/utils/";
import { useExperiencesData } from "./hooks/useExperiencesData";
import { ExperiencesTable } from "./components/ExperiencesTable";
import { InsightsPanel } from "./components/InsightsPanel/InsightsPanel";
import {
  DEFAULT_VISIBLE_COLUMNS,
  EXPERIENCE_COLUMNS,
  MAX_VISIBLE_COLUMNS,
  normalizeVisibleColumns,
  type ExperienceColumnKey,
} from "./components/experienceColumns";
import { MediaProjectListItem, ProjectStatus } from "../types";
import { TableFilters } from "helpers/table/TableFilters";
import type { QuickFilterPill } from "helpers/table/QuickFilters";
import { useAntTheme } from "hooks/useAntTheme/";
import { colors } from "@libertify/ui/theme-v2";
import { radius } from "@libertify/ui";
import { PageLayout, TABLE_THEME } from "components/PageLayout";

// Status pills are mutually exclusive — each maps to a single server-side
// ProjectStatus (or none for "all").
export type StatusFilterKey = "all" | "published" | "ready";

const STATUS_LABEL_KEYS: Record<StatusFilterKey, string> = {
  all: "media.components.projectQuickAccess.all",
  published: "media.components.MediaStatus.Published",
  ready: "media.components.projectQuickAccess.ready",
};

const STATUS_FILTER_KEYS = Object.keys(STATUS_LABEL_KEYS) as StatusFilterKey[];

// Pinned columns (e.g. experience name) can't be toggled.
const REMOVABLE_COLUMN_KEYS = new Set(
  EXPERIENCE_COLUMNS.filter((c) => c.removable).map((c) => c.key)
);

// Each status pill maps to one server-side ProjectStatus (or none).
const STATUS_BY_FILTER: Record<StatusFilterKey, ProjectStatus | undefined> = {
  all: undefined,
  published: "Published",
  ready: "MediaGenerated",
};

// Default sort; the "Last modified" toggle swaps to `updated`.
const CREATED_SORT: CrudSort = { field: "created", order: "desc" };
const LAST_MODIFIED_SORT: CrudSort = { field: "updated", order: "desc" };

// Emits the full filter set (search + status) so callers can use "replace"
// without one input clobbering the other.
const buildFilters = (
  search: string,
  status: StatusFilterKey
): CrudFilter[] => {
  const filters: CrudFilter[] = [];
  if (search) {
    filters.push({ field: "search_text", operator: "eq", value: search });
  }
  const projectStatus = STATUS_BY_FILTER[status];
  if (projectStatus) {
    filters.push({ field: "status", operator: "eq", value: projectStatus });
  }
  return filters;
};

export const OrganizationAnalyticsPage = () => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const go = useGo();
  const { currentOrganization } = useSession();
  const organizationId = currentOrganization?.id;
  const { workspaceId } = useWorkspace();

  // Visible table columns, capped at MAX_VISIBLE_COLUMNS to avoid overflow.
  const [visibleColumns, setVisibleColumns] = useState<ExperienceColumnKey[]>(
    DEFAULT_VISIBLE_COLUMNS
  );
  const [searchText, setSearchText] = useState("");
  const [activeStatus, setActiveStatus] = useState<StatusFilterKey>("all");
  const [sortByLastModified, setSortByLastModified] = useState(false);

  const toggleColumn = useCallback((key: ExperienceColumnKey) => {
    if (!REMOVABLE_COLUMN_KEYS.has(key)) return;
    setVisibleColumns((prev) => {
      const isOn = prev.includes(key);
      if (!isOn && prev.length >= MAX_VISIBLE_COLUMNS) return prev;
      return isOn
        ? prev.filter((k) => k !== key)
        : normalizeVisibleColumns([...prev, key]);
    });
  }, []);

  const {
    summariesMap,
    tableProps,
    setCurrent,
    setFilters,
    setSorters,
    isLoadingSummaries,
  } = useExperiencesData(organizationId, workspaceId);

  const {
    total: activeNow,
    isLoading: isLoadingActiveNow,
    isError: isErrorActiveNow,
  } = useLiveViewerCount(organizationId);

  // Map project id → title for the insights panel's experience link resolution.
  const projectTitleById = useMemo(() => {
    const items = tableProps.dataSource ?? [];
    return Object.fromEntries(items.map((p) => [p.id, p.title]));
  }, [tableProps.dataSource]);

  // Avg document_reading_depth_pct across the loaded page; null → card shows "-".
  const avgReadDepth = useMemo(() => {
    const values = Array.from(summariesMap.values())
      .map((s) => Number(s.document_reading_depth_pct))
      .filter((n) => !Number.isNaN(n));
    if (values.length === 0) return null;
    return values.reduce((sum, n) => sum + n, 0) / values.length;
  }, [summariesMap]);

  // Re-apply search + status + sort together, resetting to page 1.
  const applyFilters = useCallback(
    (search: string, status: StatusFilterKey, lastModified: boolean) => {
      setCurrent(1);
      setFilters(buildFilters(search, status), "replace");
      setSorters([lastModified ? LAST_MODIFIED_SORT : CREATED_SORT]);
    },
    [setCurrent, setFilters, setSorters]
  );

  const debouncedSearch = useDebouncedCallback((value: string) => {
    applyFilters(value, activeStatus, sortByLastModified);
  }, 300);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchText(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handleStatusChange = useCallback(
    (key: StatusFilterKey) => {
      setActiveStatus(key);
      applyFilters(searchText, key, sortByLastModified);
    },
    [applyFilters, searchText, sortByLastModified]
  );

  const handleLastModifiedToggle = useCallback(() => {
    const next = !sortByLastModified;
    setSortByLastModified(next);
    applyFilters(searchText, activeStatus, next);
  }, [applyFilters, searchText, activeStatus, sortByLastModified]);

  const quickFilterPills: QuickFilterPill[] = useMemo(
    () => [
      ...STATUS_FILTER_KEYS.map((key) => ({
        key,
        label: t(STATUS_LABEL_KEYS[key]),
        active: activeStatus === key,
        onClick: () => handleStatusChange(key),
      })),
      {
        key: "lastModified",
        label: t("media.analytics.quickFilters.lastModified"),
        active: sortByLastModified,
        onClick: handleLastModifiedToggle,
      },
    ],
    [
      t,
      activeStatus,
      sortByLastModified,
      handleStatusChange,
      handleLastModifiedToggle,
    ]
  );

  const fieldColumns = useMemo(
    () =>
      EXPERIENCE_COLUMNS.map((c) => ({
        key: c.key,
        label: t(`media.analytics.columns.${c.labelKey}`),
        removable: c.removable,
      })),
    [t]
  );

  const isColumnVisible = useCallback(
    (key: ExperienceColumnKey) => visibleColumns.includes(key),
    [visibleColumns]
  );

  const handleExperienceClick = useCallback(
    (record: MediaProjectListItem) => {
      go({ to: `/media/projects/${record.id}/analytics` });
    },
    [go]
  );

  return (
    <Authenticated key="organizationAnalytics">
      <PageLayout
        title={t("media.analytics.title")}
        description={t("media.analytics.description")}
        breadcrumbLabel={t("src.App.analytics")}
      >
        <Row gutter={theme.margin} align="stretch">
          <Col span={12}>
            <StatCard
              title={t("media.analytics.activeNow")}
              value={isErrorActiveNow ? "—" : activeNow}
              description={t("media.analytics.activeNowDescription")}
              loading={isLoadingActiveNow}
            />
          </Col>
          <Col span={12}>
            <StatCard
              title={t("media.analytics.avgReadDepth")}
              value={avgReadDepth == null ? "—" : formatPct(avgReadDepth)}
              description={t("media.analytics.avgReadDepthDescription")}
              loading={isLoadingSummaries}
            />
          </Col>
        </Row>
        {/* Filters, table and insights in Inter (only the font token changes). */}
        <ConfigProvider theme={TABLE_THEME}>
          <Flex gap={theme.marginLG} align="flex-start">
            <Space
              direction="vertical"
              size={0}
              style={{
                flex: 1,
                minWidth: 0,
                backgroundColor: colors.surface.base,
                borderRadius: radius.box,
                border: `1px solid ${colors.border.subtle}`,
                overflow: "hidden",
              }}
            >
              <TableFilters
                quickFilters={{
                  label: t("media.analytics.quickFilters.label"),
                  pills: quickFilterPills,
                }}
                fieldsDropdown={{
                  columns: fieldColumns,
                  isVisible: isColumnVisible,
                  toggleColumn,
                  canAddMore: visibleColumns.length < MAX_VISIBLE_COLUMNS,
                  title: t("media.analytics.tableFields.title"),
                  buttonLabel: t("media.analytics.tableFields.button"),
                  hint: t("media.analytics.tableFields.hint", {
                    count: MAX_VISIBLE_COLUMNS,
                  }),
                }}
                searchValue={searchText}
                onSearchChange={handleSearchChange}
                searchPlaceholder={t("media.analytics.searchPlaceholder")}
              />
              <ExperiencesTable
                tableProps={tableProps}
                summariesMap={summariesMap}
                visibleColumns={visibleColumns}
                onExperienceClick={handleExperienceClick}
              />
            </Space>
            {organizationId && (
              <InsightsPanel
                organizationId={organizationId}
                workspaceId={workspaceId}
                projectTitleById={projectTitleById}
              />
            )}
          </Flex>
        </ConfigProvider>
      </PageLayout>
    </Authenticated>
  );
};
